/**
 * @fileoverview Plugin per la registrazione globale dei componenti Vue
 *
 * Questo plugin registra automaticamente tutti i componenti riutilizzabili
 * come componenti globali di Vue, rendendoli disponibili in tutta l'applicazione
 * senza bisogno di importarli esplicitamente in ogni componente.
 *
 * @author Generated by TypeScript Conversion
 * @since 2025-09-23
 */

import Vue from 'vue';
import type { PluginFunction } from 'vue';
// VueConstructor temporaneamente non utilizzato

// Import componenti di layout per search e tabelle
import ElTableDraggable from 'element-ui-el-table-draggable';
import SearchLayout from '@/components/layout/SearchLayout.vue';
import SearchResultsHeaderLayout from '@/components/layout/SearchResultsHeaderLayout.vue';
import SearchResultsTableLayout from '@/components/layout/SearchResultsTableLayout.vue';

// Import componenti base riutilizzabili
import Pagination from '@/components/Pagination.vue';
import SortableColumnHeader from '@/components/SortableColumnHeader.vue';
import LegendaTooltip from '@/components/modules/common/LegendaTooltip.vue';
import PlanningItem from '@/components/PlanningItem.vue';

// Import componenti per tabelle Element UI
import ElTableColumnSortable from '@/components/ElTableColumnSortable.vue';
import ElTableColumnCodeDescription from '@/components/ElTableColumnCodeDescription.vue';
import ElTableColumnTwoFields from '@/components/ElTableColumnTwoFields.vue';
import ElTableColumnClientStatus from '@/components/ElTableColumnClientStatus.vue';
import ElTableColumnActions from '@/components/ElTableColumnActions.vue';

// Import componenti per azioni e form
import RowAction from '@/components/RowAction.vue';
import ActionsGroup from '@/components/ActionsGroup.vue';
import ElInputNumberWrapper from '@/components/ElInputNumberWrapper.vue';
import ElSelectLink from '@/components/ElSelectLink.vue';

// Import plugin esterni

// Import servizi come plugin Vue
import Modal from '@/lib/services/modal/index';
import Notification from '@/lib/services/notification/index';
import DefaultPickerOptions from '@/lib/services/elementDefaultPickerOptions';

/**
 * Interfaccia per un componente Vue registrabile globalmente
 */
interface GlobalComponent {
	/** Nome del componente da usare nei template */
	name: string;
	/** Il componente Vue stesso (supporta SFC e costruttori) */
	component: any; // Usiamo any per compatibilità con SFC
}

/**
 * Interfaccia per un plugin Vue da installare
 */
interface VuePlugin {
	/** Nome descrittivo del plugin */
	name: string;
	/** Il plugin Vue da installare */
	plugin: PluginFunction<any> | { install: PluginFunction<any> };
}

/**
 * Configurazione dei componenti globali da registrare
 *
 * Organizzati per categoria per facilitare manutenzione e debugging.
 * Ogni componente viene registrato con Vue.component() e diventa
 * disponibile globalmente in tutti i template.
 */
const GLOBAL_COMPONENTS: readonly GlobalComponent[] = [
	// =========================================================================
	// LAYOUT COMPONENTS - Strutture di layout per pagine di ricerca
	// =========================================================================
	{
		name: 'search-layout',
		component: SearchLayout,
	},
	{
		name: 'search-results-header-layout',
		component: SearchResultsHeaderLayout,
	},
	{
		name: 'search-results-table-layout',
		component: SearchResultsTableLayout,
	},

	// =========================================================================
	// BASE COMPONENTS - Componenti base riutilizzabili
	// =========================================================================
	{
		name: 'LegendaTooltip',
		component: LegendaTooltip,
	},
	{
		name: 'pagination',
		component: Pagination,
	},
	{
		name: 'SortableColumnHeader',
		component: SortableColumnHeader,
	},
	{
		name: 'planning-item',
		component: PlanningItem,
	},

	// =========================================================================
	// ELEMENT UI TABLE COMPONENTS - Colonne specializzate per el-table
	// =========================================================================
	{
		name: 'el-table-column-sortable',
		component: ElTableColumnSortable,
	},
	{
		name: 'el-table-column-code-description',
		component: ElTableColumnCodeDescription,
	},
	{
		name: 'el-table-column-two-fields',
		component: ElTableColumnTwoFields,
	},
	{
		name: 'el-table-column-client-status',
		component: ElTableColumnClientStatus,
	},
	{
		name: 'el-table-column-actions',
		component: ElTableColumnActions,
	},
	{
		name: 'el-table-draggable',
		component: ElTableDraggable,
	},

	// =========================================================================
	// ACTION COMPONENTS - Componenti per azioni utente
	// =========================================================================
	{
		name: 'row-action',
		component: RowAction,
	},
	{
		name: 'actions-group',
		component: ActionsGroup,
	},

	// =========================================================================
	// FORM COMPONENTS - Wrapper per componenti form Element UI
	// =========================================================================
	{
		name: 'el-input-number-wrapper',
		component: ElInputNumberWrapper,
	},
	{
		name: 'el-select-link',
		component: ElSelectLink,
	},
] as const;

/**
 * Configurazione dei plugin Vue da installare
 *
 * Servizi globali che estendono Vue con funzionalità aggiuntive
 * come modal, notifiche e configurazioni default per Element UI.
 */
const VUE_PLUGINS: readonly VuePlugin[] = [
	{
		name: 'Modal Service',
		plugin: Modal,
	},
	{
		name: 'Notification Service',
		plugin: Notification,
	},
	{
		name: 'Default Picker Options',
		plugin: DefaultPickerOptions,
	},
] as const;

/**
 * Registra un singolo componente globalmente
 *
 * @param {string} name - Nome del componente
 * @param {any} component - Componente Vue da registrare (SFC o costruttore)
 *
 * @example
 * ```typescript
 * registerGlobalComponent('MyComponent', MyComponent);
 * // Ora <MyComponent /> è disponibile in tutti i template
 * ```
 */
function registerGlobalComponent(name: string, component: any): void {
	try {
		Vue.component(name, component);
	} catch (error) {
		console.error(`[GlobalComponents] Failed to register component ${name}:`, error);
	}
}

/**
 * Installa un plugin Vue
 *
 * @param {string} name - Nome descrittivo del plugin
 * @param {PluginFunction<any> | { install: PluginFunction<any> }} plugin - Plugin da installare
 *
 * @example
 * ```typescript
 * installVuePlugin('MyPlugin', MyPlugin);
 * // Ora il plugin è disponibile globalmente
 * ```
 */
function installVuePlugin(
	name: string,
	plugin: PluginFunction<any> | { install: PluginFunction<any> },
): void {
	try {
		Vue.use(plugin);
	} catch (error) {
		console.error(`[GlobalComponents] Failed to install plugin ${name}:`, error);
	}
}

/**
 * Plugin Nuxt per la registrazione automatica di componenti globali
 *
 * Questo plugin viene eseguito automaticamente all'avvio di Nuxt e registra
 * tutti i componenti riutilizzabili come globali, installando anche i servizi
 * necessari per il funzionamento dell'applicazione.
 *
 * **Benefici dei Componenti Globali:**
 * - 🚀 **No Import Required** - Disponibili automaticamente
 * - 🎯 **Consistent Naming** - Nomi standardizzati in tutta l'app
 * - 🔄 **Easy Maintenance** - Gestione centralizzata
 * - 📦 **Bundle Optimization** - Condivisione efficiente
 *
 * **Componenti Registrati:**
 * - Layout components per pagine di ricerca
 * - Base components riutilizzabili (Pagination, SortableColumnHeader, etc.)
 * - Element UI table columns specializzate
 * - Action components per operazioni utente
 * - Form wrappers per Element UI
 *
 * **Servizi Installati:**
 * - Modal Service per dialog e popup
 * - Notification Service per messaggi utente
 * - Default Picker Options per date/time pickers
 *
 * @example
 * ### Utilizzo nei Template
 * ```vue
 * <template>
 *   <div>
 *     <!-- Disponibili automaticamente, senza import -->
 *     <search-layout
 *       storage-key="my-page"
 *       :loading="isTableLoading"
 *       @search="triggerSearch"
 *       @flush-filter="onFlushFilter"
 *     >
 *       <template #custom-actions>
 *         <actions-group :actions="myActions" />
 *       </template>
 *
 *       <template #search-result>
 *         <el-table :data="tableData">
 *           <el-table-column-sortable prop="name" label="Nome" />
 *           <el-table-column-actions :actions="rowActions" />
 *         </el-table>
 *         <Pagination v-model="pagination" />
 *       </template>
 *     </search-layout>
 *   </div>
 * </template>
 * ```
 *
 * @example
 * ### Utilizzo dei Servizi
 * ```typescript
 * // Modal Service
 * this.$modal.confirm('Confermare l\'operazione?')
 *   .then(() => console.log('Confermato'));
 *
 * // Notification Service
 * this.$notification.success('Operazione completata!');
 * this.$notification.error('Si è verificato un errore');
 * ```
 *
 * @example
 * ### Aggiunta di Nuovi Componenti
 * ```typescript
 * // Aggiungere a GLOBAL_COMPONENTS:
 * {
 *   name: 'MyNewComponent',
 *   component: MyNewComponent
 * }
 *
 * // Il componente sarà automaticamente disponibile come:
 * // <MyNewComponent /> in tutti i template
 * ```
 */
export default function globalComponentsPlugin(): void {
	let failedComponents = 0;

	// Registrazione componenti globali
	for (const { name, component } of GLOBAL_COMPONENTS) {
		try {
			registerGlobalComponent(name, component);
		} catch (error) {
			console.error(`Failed to register component ${name}:`, error);
			failedComponents++;
		}
	}
	console.groupEnd();

	let failedPlugins = 0;

	// Installazione plugin Vue
	for (const { name, plugin } of VUE_PLUGINS) {
		try {
			installVuePlugin(name, plugin);
		} catch (error) {
			console.error(`Failed to install plugin ${name}:`, error);
			failedPlugins++;
		}
	}
	console.groupEnd();

	// Warning se ci sono stati errori
	if (failedComponents > 0 || failedPlugins > 0) {
		console.warn('[GlobalComponents] Some registrations failed. Check the logs above for details.');
	}
}

/**
 * Esporta tipi per uso esterno
 */
export type { GlobalComponent, VuePlugin };

/**
 * Esporta liste per riferimento esterno (es. testing, documentazione)
 */
export { GLOBAL_COMPONENTS, VUE_PLUGINS };
